import { defineComponent as _defineComponent } from 'vue'
import { ref, type Ref, type ComponentPublicInstance, watch, nextTick, onUnmounted } from 'vue';
import CColorPicker from '@/components/shared/CColorPicker';
import CButton from '@/components/shared/CButton';
import { type FullColorObject, type PaletteColorObject, type GradientStopObject, type ColorPickerTypeValue, ColorPickerType, useColor, CColorPickerValue } from '@/composables/color';
import type { ColorObject, GradientObject, PaletteObject } from '@/typings/libraries';
import type { FontColorOption } from '@/typings/customilyElements/canvasElements';


export default /*#__PURE__*/_defineComponent({
  __name: 'ColorSelector',
  props: {
    open: { type: Boolean },
    color: null,
    isCmyk: { type: Boolean },
    enablePalette: { type: Boolean },
    hideGradient: { type: Boolean },
    showMenuTypes: { type: Boolean }
  },
  emits: ["color-selected", "color-changed", "cancel"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'color-selected', color: FontColorOption): void;
    (e: 'color-changed', color: FontColorOption): void;
    (e: 'cancel'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    open?: boolean;
    color?: FontColorOption;
    isCmyk?: boolean;
    enablePalette?: boolean;
    hideGradient?: boolean;
    showMenuTypes?: boolean;
};





const colord = useColor();
const currentColor: Ref<CColorPickerValue | undefined> = ref();
const currentType: Ref<ColorPickerTypeValue> = ref(ColorPickerType.SOLID);
const wrapperRef = ref<HTMLElement>();
const pickerRef = ref<ComponentPublicInstance>();
const opensBelow = ref(false);
let resizeObserver: ResizeObserver | undefined;

const createColorWithCmyk = (colorItem: { hex: string; alpha: number }) => {
    const color = colord.getColorObj({ hex: colorItem.hex, a: colorItem.alpha });
    return {
        ...colorItem,
        cmyk: color?.cmyk || { c: 0, m: 0, y: 0, k: 0 }
    };
};

// Same source as a solid color: in CMYK mode the stored CMYK is the one the user typed, otherwise it comes from the hex
const createPaletteColorWithCmyk = (colorItem: ColorObject) => {
    if (props.isCmyk && typeof colorItem.cmyk === 'string') {
        return { ...colorItem, cmyk: colord.cmykStringToObject(colorItem.cmyk) };
    }
    return createColorWithCmyk(colorItem);
};

const createColorWithCmykString = (v: GradientStopObject | PaletteColorObject) => {
    return {
        ...v,
        cmyk: v.cmyk ? colord.cmykToDecimalString(v.cmyk) : undefined
    }
};

const getFontColorValue = (color: CColorPickerValue): FontColorOption => {
    let value: FontColorOption;

    if ('colorStops' in color) {
        const colorStops = color.colorStops.map(createColorWithCmykString);
        value = {
            ...color,
            colorStops
        } as GradientObject;
    } else if ('colors' in color) {
        const colors = color.colors.map(createColorWithCmykString);
        value = {
            ...color,
            colors
        } as PaletteObject;
    } else {
        value = {
            hex: color.hex,
            alpha: color.a,
            cmyk: colord.cmykToDecimalString(color.cmyk)
        } as ColorObject;
    }

    return value;
}

const onColorSelected = (preview?: boolean) => {
    if (!currentColor.value) return;

    const color = getFontColorValue(currentColor.value);

    if (preview) {
        emit('color-changed', color);
        return;
    }

    emit('color-selected', color);
};

const onClearSelectedColor = () => {
    currentColor.value = undefined;
    currentType.value = ColorPickerType.SOLID;
};

watch(() => props.color, (val) => {
    if (!val) {
        onClearSelectedColor();
        return;
    }

    if ('colorStops' in val) {
        const stops = val.colorStops.map(createColorWithCmyk) as GradientStopObject[];
        currentColor.value = { ...val, colorStops: stops };
        currentType.value = ColorPickerType.GRADIENT;
    } else if ('colors' in val) {
        const colors = val.colors.map(createPaletteColorWithCmyk) as PaletteColorObject[];
        currentColor.value = { ...val, colors };
        currentType.value = ColorPickerType.PALETTE;
    } else {
        const color = val as ColorObject;
        const value: Partial<FullColorObject> = props.isCmyk ? { cmyk: colord.cmykStringToObject(color.cmyk) } : { hex: color.hex };
        currentColor.value = colord.getColorObj({ ...value, a: (val as ColorObject).alpha });
        currentType.value = ColorPickerType.SOLID;
    }
}, { immediate: true });

const getClipBounds = (el: HTMLElement) => {
    let top = 0;
    let bottom = window.innerHeight;
    let scroller: HTMLElement | undefined;
    let clipped = false;

    for (let parent = el.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
        const { overflowY, position } = getComputedStyle(parent);

        if (overflowY !== 'visible') {
            // Client box: a horizontal scrollbar or border would hide the picker's edge
            const clipTop = parent.getBoundingClientRect().top + parent.clientTop;
            top = Math.max(top, clipTop);
            bottom = Math.min(bottom, clipTop + parent.clientHeight);
            // Only the nearest clipper can reveal the picker: scrolling further out, it stays clipped
            if (!clipped && /auto|scroll/.test(overflowY)) scroller = parent;
            clipped = true;
        }
        if (position === 'fixed') break;
    }

    return { top, bottom, scroller };
};

const placePicker = async () => {
    const wrapper = wrapperRef.value;
    const picker = pickerRef.value?.$el as HTMLElement | undefined;
    if (!wrapper || !picker) return;

    const { top, bottom, scroller } = getClipBounds(wrapper);

    if (!opensBelow.value) {
        const wrapperTop = wrapper.getBoundingClientRect().top;
        const spaceAbove = wrapperTop - top;
        if (picker.offsetHeight <= spaceAbove) return;
        // Clipped content above can't be scrolled to, below it can
        if (!scroller && bottom - wrapperTop < spaceAbove) return;
        opensBelow.value = true;
        await nextTick();
    }

    const pickerRect = picker.getBoundingClientRect();
    // Taller than the visible area: keep its top in view
    const scrollBy = Math.min(pickerRect.bottom - bottom, pickerRect.top - top);
    if (scroller && scrollBy > 0) scroller.scrollTop += scrollBy;
};

// The first callback covers opening; later ones, height changes from switching picker type
watch(pickerRef, (picker) => {
    resizeObserver?.disconnect();
    opensBelow.value = false;
    if (!picker) return;

    resizeObserver = new ResizeObserver(placePicker);
    resizeObserver.observe(picker.$el);
});

onUnmounted(() => {
    resizeObserver?.disconnect();
    onClearSelectedColor();
});

return { __sfc: true,props, emit, colord, currentColor, currentType, wrapperRef, pickerRef, opensBelow, resizeObserver, createColorWithCmyk, createPaletteColorWithCmyk, createColorWithCmykString, getFontColorValue, onColorSelected, onClearSelectedColor, getClipBounds, placePicker, CColorPicker, CButton }
}

})